import React, { useRef, useMemo, useCallback, Suspense, useState } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { Vector3, Group, Points, BufferGeometry, Float32BufferAttribute, PointsMaterial, Vector2, LineBasicMaterial, EdgesGeometry, LineSegments, SphereGeometry, Raycaster } from 'three';

interface SphereProps {
  mousePos: { x: number; y: number };
  onRotationSpeedChange: (speed: number) => void;
  isDesktop: boolean;
}

const InteractiveSphere: React.FC<SphereProps> = ({ mousePos, onRotationSpeedChange, isDesktop }) => {
  const sphereRef = useRef<Group>(null);
  const wireframeRef = useRef<LineSegments>(null);
  const spiderWebRef = useRef<LineSegments>(null);
  const baseRotationSpeed = 0.003;
  
  const { size, camera, gl, scene } = useThree();
  
  // Create sphere geometry for wireframe - much smaller on mobile
  const sphereGeometry = useMemo(() => {
    const isMobileDevice = window.innerWidth <= 768;
    const radius = isMobileDevice ? 1.2 : 1.8;
    return new SphereGeometry(radius, 32, 16);
  }, []);
  
  // Create wireframe geometry
  const wireframeGeometry = useMemo(() => {
    return new EdgesGeometry(sphereGeometry);
  }, [sphereGeometry]);
  
  // Wireframe material
  const wireframeMaterial = useMemo(() => {
    return new LineBasicMaterial({
      color: 0x8cb4b6,
      transparent: true,
      opacity: 0.6,
      linewidth: 1
    });
  }, []);
  
  // Spider web geometry for mouse interaction
  const spiderWebGeometry = useMemo(() => {
    const geometry = new BufferGeometry();
    const positions: number[] = [];
    const maxConnections = 50;
    
    // Pre-allocate space for web lines
    for (let i = 0; i < maxConnections * 6; i++) {
      positions.push(0, 0, 0);
    }
    
    geometry.setAttribute('position', new Float32BufferAttribute(positions, 3));
    return geometry;
  }, []);
  
  // Spider web material
  const spiderWebMaterial = useMemo(() => {
    return new LineBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.7,
      linewidth: 1
    });
  }, []);
  
  // No more mouse interaction with planet - removed
  
  
  // Animation loop with performance throttling
  useFrame((state, delta) => {
    if (!sphereRef.current) return;
    
    // Limit delta to prevent large jumps on slow frames
    const clampedDelta = Math.min(delta, 0.016); // Max 60fps equivalent
    
    // Simple constant rotation
    sphereRef.current.rotation.y += baseRotationSpeed * (clampedDelta * 60);
    
    // Subtle floating effect (throttled)
    if (state.clock.elapsedTime % 0.1 < 0.016) { // Update every ~6 frames
      sphereRef.current.position.y = Math.sin(state.clock.elapsedTime * 0.5) * 0.1;
    }
    
    // No mouse interaction with planet anymore - spider web always hidden
    if (spiderWebRef.current && spiderWebRef.current.material instanceof LineBasicMaterial) {
      spiderWebRef.current.material.opacity = 0;
    }
    
  });
  
  return (
    <group ref={sphereRef}>
      {/* Invisible sphere for raycasting */}
      <mesh geometry={sphereGeometry} visible={false}>
        <meshBasicMaterial transparent opacity={0} />
      </mesh>
      
      {/* Wireframe sphere */}
      <lineSegments ref={wireframeRef} geometry={wireframeGeometry} material={wireframeMaterial} />
      
      {/* Spider web effect */}
      <lineSegments ref={spiderWebRef} geometry={spiderWebGeometry} material={spiderWebMaterial} />
    </group>
  );
};

interface ThreeJSSphereProps {
  mousePos: { x: number; y: number };
  onRotationSpeedChange?: (speed: number) => void;
  isDesktop: boolean;
}

const ThreeJSSphere: React.FC<ThreeJSSphereProps> = ({ 
  mousePos, 
  onRotationSpeedChange = () => {},
  isDesktop
}) => {
  return (
    <div 
      style={{ 
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 1
      }}
    >
      <Canvas
        camera={{ position: [0, 0, 5], fov: 60 }}
        gl={{
          antialias: window.devicePixelRatio < 2, // Disable on high DPI for performance
          alpha: true,
          powerPreference: "high-performance",
          stencil: false,
          depth: false
        }}
        dpr={[1, Math.min(2, window.devicePixelRatio)]} // Smart DPR limiting
        performance={{ 
          min: 0.5,
          max: 1,
          debounce: 200
        }}
        frameloop="always" // Continuous animation
      >
        <Suspense fallback={null}>
          <ambientLight intensity={0.5} />
          <pointLight position={[10, 10, 10]} intensity={0.8} />
          <InteractiveSphere 
            mousePos={mousePos} 
            onRotationSpeedChange={onRotationSpeedChange}
            isDesktop={isDesktop}
          />
        </Suspense>
      </Canvas>
    </div>
  );
};

export default ThreeJSSphere;